import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  TableContainer,
  Flex,
  Badge,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Collapse,
  useDisclosure,
  Select,
  IconButton,
} from '@chakra-ui/react'
import { useAcompanhamentoApas } from '../hooks/AcompanhamentoApasContext'
import { LinhaAcompanhamentoApas } from './LinhaAcompanhamentoApas'
import { FiFilter, FiChevronDown, FiChevronUp, FiRefreshCw } from 'react-icons/fi'
import { useDebounce } from '../../../../hooks/useDebounce'

export const TabelaAcompanhamentoApas = () => {
  const { clientes = [], isLoading, error, isRefetching, filtros, aplicarFiltros, refetch } = useAcompanhamentoApas()

  const [searchParams, setSearchParams] = useSearchParams()

  const temFiltrosURL = searchParams.toString().length > 0
  const { isOpen, onToggle, onOpen } = useDisclosure({ defaultIsOpen: temFiltrosURL })

  const [filtrosLocais, setFiltrosLocais] = useState({
    data_inicial: searchParams.get('data_inicial') || '',
    data_final: searchParams.get('data_final') || '',
    nome_cliente: searchParams.get('nome_cliente') || '',
    empresa: searchParams.get('empresa') || '',
    vendedor: searchParams.get('vendedor') || '',
  })

  // Aplica debounce nas pesquisas de texto
  const debouncedNomeCliente = useDebounce(filtrosLocais.nome_cliente, 500)
  const debouncedEmpresa = useDebounce(filtrosLocais.empresa, 500)
  const debouncedVendedor = useDebounce(filtrosLocais.vendedor, 500)

  const [isAtualizando, setIsAtualizando] = useState(false)

  useEffect(() => {
    if (temFiltrosURL) {
      aplicarFiltros(filtrosLocais)
      onOpen()
    }
  }, [])

  // Aplicar filtros automaticamente quando os valores com debounce mudarem
  useEffect(() => {
    const filtrosAutomaticos = {
      ...filtrosLocais,
      nome_cliente: debouncedNomeCliente,
      empresa: debouncedEmpresa,
      vendedor: debouncedVendedor,
    }

    aplicarFiltros(filtrosAutomaticos)

    // Atualiza os parâmetros da URL
    const params = new URLSearchParams()
    Object.entries(filtrosAutomaticos).forEach(([key, value]) => {
      if (value) {
        params.set(key, value)
      }
    })
    setSearchParams(params, { replace: true })
  }, [debouncedNomeCliente, debouncedEmpresa, debouncedVendedor, filtrosLocais.data_inicial, filtrosLocais.data_final])

  const handleFiltroChange = (e) => {
    const { name, value } = e.target
    setFiltrosLocais({
      ...filtrosLocais,
      [name]: value,
    })
  }

  const handleLimparFiltros = () => {
    const filtrosVazios = {
      data_inicial: '',
      data_final: '',
      nome_cliente: '',
      empresa: '',
      vendedor: '',
    }

    setFiltrosLocais(filtrosVazios)
    setSearchParams({})
    aplicarFiltros(filtrosVazios)
  }

  const handleAtualizar = async () => {
    setIsAtualizando(true)
    try {
      await refetch()
    } finally {
      setIsAtualizando(false)
    }
  }

  if (isLoading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" color="#8CC63F" />
        <Text mt={4}>Carregando clientes APAS...</Text>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <Text>Erro ao carregar os clientes APAS. Por favor, tente novamente.</Text>
      </Alert>
    )
  }

  return (
    <Box>
      <Box mb={6}>
        <Flex justify="space-between" align="center" mb={2}>
          <Button onClick={onToggle} variant="outline" size="sm" leftIcon={isOpen ? <FiChevronUp /> : <FiChevronDown />}>
            {isOpen ? 'Ocultar filtros' : 'Mostrar filtros'}
          </Button>

          <HStack>
            <Button
              leftIcon={<FiRefreshCw />}
              size="sm"
              colorScheme="blue"
              onClick={handleAtualizar}
              isLoading={isAtualizando || isRefetching}
              loadingText="Atualizando..."
            >
              Atualizar
            </Button>
            <Text fontWeight="medium">Total de clientes: {clientes.length}</Text>
          </HStack>
        </Flex>

        <Collapse in={isOpen} animateOpacity>
          <Box width="100%" p={4} borderWidth="1px" borderRadius="lg" mt={2}>
            <Text fontWeight="bold" mb={4}>
              Filtros
            </Text>

            <HStack spacing={4} flexWrap="wrap">
              <FormControl w="full" maxW="200px">
                <FormLabel>Data Inicial</FormLabel>
                <Input name="data_inicial" type="date" value={filtrosLocais.data_inicial} onChange={handleFiltroChange} />
              </FormControl>

              <FormControl w="full" maxW="200px">
                <FormLabel>Data Final</FormLabel>
                <Input name="data_final" type="date" value={filtrosLocais.data_final} onChange={handleFiltroChange} />
              </FormControl>

              <FormControl w="full" maxW="250px">
                <FormLabel>Nome do Cliente</FormLabel>
                <Input name="nome_cliente" placeholder="Digite para buscar..." value={filtrosLocais.nome_cliente} onChange={handleFiltroChange} />
              </FormControl>

              <FormControl w="full" maxW="250px">
                <FormLabel>Empresa</FormLabel>
                <Input name="empresa" placeholder="Digite para buscar..." value={filtrosLocais.empresa} onChange={handleFiltroChange} />
              </FormControl>

              <FormControl w="full" maxW="250px">
                <FormLabel>Vendedor</FormLabel>
                <Input name="vendedor" placeholder="Digite para buscar..." value={filtrosLocais.vendedor} onChange={handleFiltroChange} />
              </FormControl>

              <Flex alignSelf="flex-end" flexDir="row" alignItems="center" gap={2} mt={4}>
                <Button variant="outline" onClick={handleLimparFiltros} width="full">
                  Limpar Filtros
                </Button>
              </Flex>
            </HStack>

            {Object.values(filtros).some((f) => f) && (
              <HStack mt={4} spacing={2} flexWrap="wrap">
                <Text fontWeight="medium">Filtros aplicados:</Text>
                {filtros.data_inicial && (
                  <Badge colorScheme="green" p={1}>
                    A partir de: {new Date(filtros.data_inicial).toLocaleDateString('pt-BR')}
                  </Badge>
                )}
                {filtros.data_final && (
                  <Badge colorScheme="green" p={1}>
                    Até: {new Date(filtros.data_final).toLocaleDateString('pt-BR')}
                  </Badge>
                )}
                {filtros.nome_cliente && (
                  <Badge colorScheme="blue" p={1}>
                    Cliente: {filtros.nome_cliente}
                  </Badge>
                )}
                {filtros.empresa && (
                  <Badge colorScheme="purple" p={1}>
                    Empresa: {filtros.empresa}
                  </Badge>
                )}
                {filtros.vendedor && (
                  <Badge colorScheme="orange" p={1}>
                    Vendedor: {filtros.vendedor}
                  </Badge>
                )}
              </HStack>
            )}
          </Box>
        </Collapse>
      </Box>

      <Flex justify="flex-end" align="center" mb={4}>
        {isRefetching && (
          <Badge display="flex" alignItems="center" colorScheme="blue" p={2} borderRadius="md">
            <Spinner size="sm" mr={2} />
            <Text fontSize="sm">Atualizando dados...</Text>
          </Badge>
        )}
      </Flex>

      <TableContainer>
        <Table variant="striped" size="md" position="relative">
          <Thead bg="#378BBF">
            <Tr>
              <Th width="3%" color="white" borderBottom="none" borderRight="none"></Th>
              <Th color="white" borderBottom="none" borderRight="none">
                Data
              </Th>
              <Th color="white" borderBottom="none" borderRight="none">
                Cliente
              </Th>
              <Th color="white" borderBottom="none" borderRight="none">
                Empresa
              </Th>
              <Th color="white" borderBottom="none" borderRight="none">
                Telefone
              </Th>
              <Th color="white" borderBottom="none" borderRight="none">
                Vendedor
              </Th>
              <Th color="white" borderBottom="none" borderRight="none">
                Ações
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {clientes.length > 0 ? (
              clientes.map((cliente) => <LinhaAcompanhamentoApas key={cliente.id} cliente={cliente} />)
            ) : (
              <Tr>
                <td colSpan={7}>
                  <Text textAlign="center" py={4}>
                    Nenhum cliente encontrado
                  </Text>
                </td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  )
}
